import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  Factory as FactoryIcon,
  Inventory as InventoryIcon,
  Engineering as EngineeringIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const ProductionSteelIssue = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({
    psis_no: '',
    issue_date: '',
    steel_code: '',
    heat_no: '',
    size: '',
    quantity_issued: '',
    unit: 'KG',
    issued_to: '',
    department: '',
    work_order: '',
    job_no: '',
    die_no: '',
    customer_code: '',
    remarks: '',
    status: 'ISSUED'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [steelCodes, setSteelCodes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dies, setDies] = useState([]);
  const { api } = useAuth();

  useEffect(() => {
    fetchRecords();
    fetchSteelCodes();
    fetchCustomers();
    fetchDies();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/raw-material/psis');
      const psisData = response.data?.data?.psis || response.data?.data || response.data?.psis || [];
      setRecords(Array.isArray(psisData) ? psisData : []);
    } catch (error) {
      console.error('Error fetching PSIS records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSteelCodes = async () => {
    try {
      const response = await api.get('/masters/steels');
      setSteelCodes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching steel codes:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/masters/customers');
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchDies = async () => {
    try {
      const response = await api.get('/masters/dies');
      setDies(response.data.data || []);
    } catch (error) {
      console.error('Error fetching dies:', error);
    }
  };

  const handleAdd = () => {
    setCurrentRecord({
      psis_no: '',
      issue_date: new Date().toISOString().split('T')[0],
      steel_code: '',
      heat_no: '',
      size: '',
      quantity_issued: '',
      unit: 'KG',
      issued_to: '',
      department: '',
      work_order: '',
      job_no: '',
      die_no: '',
      customer_code: '',
      remarks: '',
      status: 'ISSUED'
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleView = (record) => {
    setCurrentRecord(record);
    setOpenViewDialog(true);
  };

  const handleSave = async () => {
    try {
      const data = { ...currentRecord };
      const numericFields = ['quantity', 'quantity_issued', 'size'];
      numericFields.forEach(field => {
        if (data[field] === '' || data[field] === undefined) data[field] = null;
      });
      if (editMode) {
        await api.put(`/raw-material/psis/${data.psis_id}`, data);
      } else {
        await api.post('/raw-material/psis', data);
      }
      fetchRecords();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving PSIS record:', error);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['PSIS No', 'Date', 'Steel Code', 'Heat No', 'Quantity', 'Unit', 'Issued To', 'Customer', 'Status'],
      ...filteredRecords.map(record => [
        record.psis_no,
        record.issue_date,
        record.steel_code,
        record.heat_no,
        record.quantity_issued,
        record.unit,
        record.issued_to || '',
        record.customer_code || '',
        record.status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'production_steel_issue.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'ISSUED':
        return { label: 'Issued', color: 'success', icon: <CheckCircleIcon /> };
      case 'PENDING':
        return { label: 'Pending', color: 'warning', icon: <PendingIcon /> };
      case 'RETURNED':
        return { label: 'Returned', color: 'info', icon: <InventoryIcon /> };
      case 'CANCELLED':
        return { label: 'Cancelled', color: 'error', icon: <CancelIcon /> };
      default:
        return { label: status, color: 'default', icon: <AssignmentIcon /> };
    }
  };

  const filteredRecords = records.filter(record =>
    record.psis_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.steel_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.heat_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.issued_to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.customer_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AssignmentIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom color="primary">
            Production Steel Issue (PSIS)
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Raw material issue tracking for production operations
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Issues
              </Typography>
              <Typography variant="h4">
                {records.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Issued
              </Typography>
              <Typography variant="h4" color="success.main">
                {records.filter(r => r.status === 'ISSUED').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {records.filter(r => r.status === 'PENDING').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Quantity (KG)
              </Typography>
              <Typography variant="h4" color="info.main">
                {records.reduce((sum, record) => sum + (parseFloat(record.quantity_issued) || 0), 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search PSIS records..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>PSIS No</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Steel Code</strong></TableCell>
                <TableCell><strong>Heat No</strong></TableCell>
                <TableCell><strong>Quantity</strong></TableCell>
                <TableCell><strong>Issued To</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No PSIS records found. Add steel issues to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecords.map((record) => {
                  const statusInfo = getStatusInfo(record.status);
                  return (
                    <TableRow key={record.psis_no} hover>
                      <TableCell>
                        <Chip 
                          label={record.psis_no} 
                          color="primary" 
                          variant="outlined" 
                          size="small"
                          icon={<AssignmentIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        {record.issue_date ? new Date(record.issue_date).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {record.steel_code}
                        </Typography>
                      </TableCell>
                      <TableCell>{record.heat_no}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {record.quantity_issued} {record.unit}
                        </Typography>
                      </TableCell>
                      <TableCell>{record.issued_to || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                          icon={statusInfo.icon}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(record)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(record)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Production Steel Issue' : 'Add New PSIS'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Issue Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PSIS Number"
                value={currentRecord.psis_no}
                onChange={(e) => setCurrentRecord({...currentRecord, psis_no: e.target.value})}
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Issue Date"
                type="date"
                value={currentRecord.issue_date}
                onChange={(e) => setCurrentRecord({...currentRecord, issue_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Material Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Material Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={steelCodes}
                getOptionLabel={(option) => `${option.steel_code} - ${option.steel_name || ''}`}
                value={steelCodes.find(s => s.steel_code === currentRecord.steel_code) || null}
                onChange={(event, newValue) => {
                  setCurrentRecord({...currentRecord, steel_code: newValue?.steel_code || ''});
                }}
                renderInput={(params) => <TextField {...params} label="Steel Code" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heat Number"
                value={currentRecord.heat_no}
                onChange={(e) => setCurrentRecord({...currentRecord, heat_no: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Size/Dimension"
                value={currentRecord.size}
                onChange={(e) => setCurrentRecord({...currentRecord, size: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity Issued"
                type="number"
                step="0.01"
                value={currentRecord.quantity_issued}
                onChange={(e) => setCurrentRecord({...currentRecord, quantity_issued: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={currentRecord.unit}
                  label="Unit"
                  onChange={(e) => setCurrentRecord({...currentRecord, unit: e.target.value})}
                >
                  <MenuItem value="KG">KG</MenuItem>
                  <MenuItem value="MT">MT</MenuItem>
                  <MenuItem value="NOS">NOS</MenuItem>
                  <MenuItem value="PIECES">PIECES</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Production Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                <FactoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Production Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Issued To"
                value={currentRecord.issued_to}
                onChange={(e) => setCurrentRecord({...currentRecord, issued_to: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={currentRecord.department}
                  label="Department"
                  onChange={(e) => setCurrentRecord({...currentRecord, department: e.target.value})}
                >
                  <MenuItem value="FORGING">Forging</MenuItem>
                  <MenuItem value="MACHINING">Machining</MenuItem>
                  <MenuItem value="HEAT_TREATMENT">Heat Treatment</MenuItem>
                  <MenuItem value="FINISHING">Finishing</MenuItem>
                  <MenuItem value="QC">Quality Control</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Work Order"
                value={currentRecord.work_order}
                onChange={(e) => setCurrentRecord({...currentRecord, work_order: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Number"
                value={currentRecord.job_no}
                onChange={(e) => setCurrentRecord({...currentRecord, job_no: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={dies}
                getOptionLabel={(option) => `${option.die_no} - ${option.die_name || ''}`}
                value={dies.find(d => d.die_no === currentRecord.die_no) || null}
                onChange={(event, newValue) => {
                  setCurrentRecord({...currentRecord, die_no: newValue?.die_no || ''});
                }}
                renderInput={(params) => <TextField {...params} label="Die Number" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => `${option.cust_code} - ${option.cust_name}`}
                value={customers.find(c => c.cust_code === currentRecord.customer_code) || null}
                onChange={(event, newValue) => {
                  setCurrentRecord({...currentRecord, customer_code: newValue?.cust_code || ''});
                }}
                renderInput={(params) => <TextField {...params} label="Customer" fullWidth />}
              />
            </Grid>

            {/* Status and Remarks */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={currentRecord.status}
                  label="Status"
                  onChange={(e) => setCurrentRecord({...currentRecord, status: e.target.value})}
                >
                  <MenuItem value="ISSUED">Issued</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="RETURNED">Returned</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={currentRecord.remarks}
                onChange={(e) => setCurrentRecord({...currentRecord, remarks: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon />
            PSIS Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              {currentRecord.psis_no}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Issue Date</Typography>
                <Typography variant="body2">
                  {currentRecord.issue_date ? new Date(currentRecord.issue_date).toLocaleDateString() : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getStatusInfo(currentRecord.status).label}
                    color={getStatusInfo(currentRecord.status).color}
                    size="small"
                    icon={getStatusInfo(currentRecord.status).icon}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Steel Code</Typography>
                <Typography variant="body2">{currentRecord.steel_code}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Heat Number</Typography>
                <Typography variant="body2">{currentRecord.heat_no}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Quantity</Typography>
                <Typography variant="body2">
                  {currentRecord.quantity_issued} {currentRecord.unit}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Size</Typography>
                <Typography variant="body2">{currentRecord.size || '-'}</Typography>
              </Grid>
              
              {currentRecord.issued_to && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Issued To</Typography>
                  <Typography variant="body2">{currentRecord.issued_to}</Typography>
                </Grid>
              )}
              
              {currentRecord.department && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Department</Typography>
                  <Typography variant="body2">{currentRecord.department}</Typography>
                </Grid>
              )}
              
              {currentRecord.work_order && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Work Order</Typography>
                  <Typography variant="body2">{currentRecord.work_order}</Typography>
                </Grid>
              )}
              
              {currentRecord.customer_code && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Customer</Typography>
                  <Typography variant="body2">{currentRecord.customer_code}</Typography>
                </Grid>
              )}
              
              {currentRecord.remarks && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Remarks</Typography>
                  <Typography variant="body2">{currentRecord.remarks}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          <Button 
            onClick={() => {
              setOpenViewDialog(false);
              handleEdit(currentRecord);
            }} 
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionSteelIssue;